import { create } from "zustand";
import { pushNotificationService } from "@/lib/push-notifications";

export type PermissionState = "prompt" | "granted" | "denied" | "unsupported";

interface NotificationPermissionStore {
  permission: PermissionState | null;
  isLoading: boolean;
  isSupported: boolean;
  canRequest: boolean;
  isGranted: boolean;
  isDenied: boolean;

  // Actions
  checkPermission: () => Promise<void>;
  requestPermission: () => Promise<boolean>;
}

export const useNotificationPermissionStore = create<NotificationPermissionStore>((set, _get) => ({
  permission: null,
  isLoading: true,
  isSupported: false,
  canRequest: false,
  isGranted: false,
  isDenied: false,

  checkPermission: async () => {
    set({ isLoading: true });

    try {
      if (!pushNotificationService.isSupported()) {
        set({
          permission: "unsupported",
          isLoading: false,
          isSupported: false,
          canRequest: false,
          isGranted: false,
          isDenied: false,
        });
        return;
      }

      const status = await pushNotificationService.getPermissionStatus();

      set({
        permission: status,
        isLoading: false,
        isSupported: true,
        canRequest: status === "prompt",
        isGranted: status === "granted",
        isDenied: status === "denied",
      });
    } catch (error) {
      console.error("Failed to check permission status:", error);
      set({
        permission: "unsupported",
        isLoading: false,
        isSupported: false,
        canRequest: false,
        isGranted: false,
        isDenied: false,
      });
    }
  },

  requestPermission: async (): Promise<boolean> => {
    try {
      const success = await pushNotificationService.requestPermission();

      // Always re-check permission status after request to ensure accuracy
      const actualStatus = await pushNotificationService.getPermissionStatus();
      const isSupported = await pushNotificationService.isSupported();

      set({
        permission: actualStatus,
        isSupported: isSupported,
        canRequest: actualStatus === "prompt",
        isGranted: actualStatus === "granted",
        isDenied: actualStatus === "denied",
      });

      return success;
    } catch (error) {
      console.error("Failed to request permission:", error);
      return false;
    }
  },
}));

// Initialize permission check on store creation
useNotificationPermissionStore.getState().checkPermission();
